/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ChangeEvent, KeyboardEvent, MouseEvent, TouchEvent, useCallback, useEffect, useRef, useState } from 'react'

import Banner, { BannerType, BannerVariaion } from '@cox/core-ui8/dist/Banner'
import { useUdoContext } from '@cox/core-ui8/dist/UdoContext'
import axios from 'axios'
import debounce from 'lodash/debounce'
import ReactDOM from 'react-dom'

import {
  CHEVRON_RIGHT_WHITE,
  CIRCLE_CHECK_LIME_GREEN_CIRCLE,
  CIRCLE_EXCLAMATION_ERROR,
  LOCATION_PIN
} from '../../../assets/images'
import '../ReactAddressCapture.scss'
import './AddressCaptureOutageMap.scss'
import { GOOGLE_OUTAGE_MAP_KEY, OUTAGE_MAP_SERVER_ERROR_MESSAGE, WEB_APIS } from '../../../constants'
import { sortArrayByKey } from '../../../utils/helper-util'
import { getTokenValues } from '../../../utils/rule-builder-util'
import { AddressServiceabilityStatus, ReactComponents, ScreenSizes } from '../../../utils/types'
import { updateTealiumStatus } from '../../../utils/utag-util'
import ReactText from '../../ReactText'
import { addressFormat } from '../AddressCapture/types'
import AddressCaptureInput from '../shared/AddressCaptureInput'
import MultipleAddressDropdown from '../shared/MultipleAddressDropdown'
import { ReactAddressCaptureProps } from '../types'
import { loadGMScript } from '../util/address-check-helper-utils'

const AddressCaptureOutageMap = (props: ReactAddressCaptureProps): JSX.Element => {
  const {
    id = '',
    theme = 'white',
    response = AddressServiceabilityStatus.default,
    messages = '',
    configuredFor = 'outagemap',
    tokenProperties,
    locationPin = LOCATION_PIN,
    simplifiedIcon = CHEVRON_RIGHT_WHITE,
    multipleAddressChevron,
    multipleAddressChevronAlt,
    inLayoutContainer = false
  } = props

  type defaultSuggestion = {
    id: ''
    label: ''
    value: ''
    normalPart: ''
    boldPart: ''
  }

  const { udo } = useUdoContext()

  const [responseKey, setResponseKey] = useState(response)
  const [suggestions, setSuggestions] = useState(Array<defaultSuggestion>())
  const [suggestionIndex, setSuggestionIndex] = useState(-1)
  const [suggestionsActive, setSuggestionsActive] = useState(false)
  const [displayInputs, setDisplayInputs] = useState(false)
  const [isMultipleAddress, setIsMultipleAddress] = useState(false)
  const [showSearchAddressInput, setShowSearchAddressInput] = useState(true)
  const [searchResponse, setSearchResponse] = useState<any | null>(null)

  const [value, setValue] = useState('')
  const [zipcode, setZipcode] = useState('')
  const [unit, setUnit] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [latValue, setLatValue] = useState<number | null>(null)
  const [langValue, setLangValue] = useState<number | null>(null)

  const [addressSearchValue, setAddressSearchValue] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<any | null>(null)
  const [addressList, setAddressList] = useState<any | null>(null)
  const [streetInputPlaceholder, setStreetInputPlaceholder] = useState('')
  const [suggestionPosition, setSuggestionPosition] = useState({ offsetLeft: 0, offsetTop: 0 })

  const [searchProgress, setSearchProgress] = useState(false)
  const [searchCompleted, setSearchCompleted] = useState(false)

  const [eyebrow, setEyebrow] = useState('')
  const [heading, setHeading] = useState('')
  const [description, setDescription] = useState('')

  const descriptionRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleResize = () => {
    const { current } = inputRef

    setTimeout(() => {
      if (current) {
        setSuggestionPosition({
          offsetLeft: current?.offsetLeft ?? 0,
          offsetTop: current?.offsetTop ?? 0
        })
      }
      setStreetInputPlaceholder(window.innerWidth < ScreenSizes.MD ? 'Street Address' : 'Street Address, Apt/Unit, Zip Code')
    }, 350)
  }

  useEffect(() => {
    loadGMScript(GOOGLE_OUTAGE_MAP_KEY)

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)

      debouncedChangeHandler.cancel()
    }
  }, [])

  useEffect(() => {
    const serviceAddressLink = document.querySelector('.show-search-field')
    if (serviceAddressLink) {
      const streetAddress = selectedAddress?.address || value
      const aptNumber = selectedAddress?.apt || unit
      const zip = selectedAddress?.zipCode || zipcode

      serviceAddressLink.addEventListener('click', (event) => {
        event.preventDefault()
        retainDefaultState(streetAddress, aptNumber, zip)
      })
    }
  }, [searchResponse])

  // TODO - create separate component for suggestions
  const Suggestions = () => {
    return (
      <ul
        className={
          'react-address-suggestion-list ui-menu ui-widget ui-widget-content ui-autocomplete ui-front bindComplete ui-input-street-address-input google-addressList ' +
          (suggestions && suggestions.length > 0 ? '' : 'd-none')
        }
        style={{ top: suggestionPosition.offsetTop + 60 + 'px', left: suggestionPosition.offsetLeft - 50 + 'px' }}
      >
        {suggestions?.map((suggestion, index) => {
          return (
            <li
              className={index === suggestionIndex ? 'ui-menu-item highlight' : 'ui-menu-item'}
              key={index}
              onMouseDown={(event) => suggestionClick(event, index)}
              onTouchStart={(event) => suggestionClick(event, index)}
            >
              <div id={'ui-id-' + index} className='ui-menu-item-wrapper google-addressList-item'>
                <span className='google-Marker'></span>
                <span className='google-highlight'>{(suggestion as any).boldPart}</span>
                {(suggestion as any).normalPart}
              </div>
            </li>
          )
        })}
        <li className='google-powered-icon'></li>
      </ul>
    )
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
        if (suggestionIndex - 1 === suggestions.length) {
          return
        }
        setSuggestionIndex(suggestionIndex + 1)
        break
      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        if (suggestionIndex === 0) {
          return
        }
        setSuggestionIndex(suggestionIndex - 1)
        break
      case 'Enter':
        //@ts-ignore
        const selectedPlaceId = suggestions[suggestionIndex].id
        //@ts-ignore
        setValue(suggestions[suggestionIndex].label)
        setSuggestionIndex(0)
        setSuggestionsActive(false)
        triggerGeocodeCall(selectedPlaceId)
        break
      default:
        return
    }
  }

  const updateGeocodeComponents = (placeResult: google.maps.GeocoderResult[], placeServiceStatus: string) => {
    if (placeResult != undefined && placeServiceStatus === 'OK') {
      const components = placeResult && placeResult[0] ? placeResult[0].address_components : []
      let street = '',
        number = '',
        zip = '',
        unit = '',
        state = '',
        city = ''

      components.forEach((component: google.maps.GeocoderAddressComponent) => {
        if (component.types[0] == 'route') {
          street = component['long_name']
        }
        if (component.types[0] == 'street_number') {
          number = component['long_name']
        }
        if (component.types[0] == 'postal_code') {
          zip = component['long_name']
        }
        if (component.types[0] == 'subpremise') {
          unit = component['long_name']
        }
        if (component.types[0] == 'administrative_area_level_1') {
          state = component['short_name']
        }
        if (component.types[0] == 'locality') {
          city = component['long_name']
        }
      })

      setLangValue(placeResult[0].geometry.location.lng())
      setLatValue(placeResult[0].geometry.location.lat())

      if (number && street) {
        setAddressSearchValue(number + ' ' + street)
        setValue(number + ' ' + street)
        setSuggestionsActive(false)
      }

      setZipcode(zip || '')
      setUnit(unit)
      setCity(city)
      setState(state)
    }
  }

  const triggerGeocodeCall = (addressPlaceId: string, multiAddress?: boolean): void => {
    const geocoder = new google.maps.Geocoder()

    if (multiAddress) {
      geocoder.geocode(
        { address: addressPlaceId },
        //@ts-ignore
        (placeResult: google.maps.GeocoderResult[], placeServiceStatus: string) => {
          updateGeocodeComponents(placeResult, placeServiceStatus)
        }
      )
    } else {
      geocoder.geocode(
        { placeId: addressPlaceId },
        //@ts-ignore
        (placeResult: google.maps.GeocoderResult[], placeServiceStatus: string) => {
          updateGeocodeComponents(placeResult, placeServiceStatus)
        }
      )
    }
  }

  const suggestionClick = (event: MouseEvent | TouchEvent, index: number) => {
    event.preventDefault()

    setSuggestionIndex(index)
    //@ts-ignore
    const selectedPlaceId = suggestions[index].id
    //@ts-ignore
    setValue(suggestions[index].label)
    setSuggestionIndex(0)
    setSuggestionsActive(false)
    triggerGeocodeCall(selectedPlaceId)
    setSuggestionsActive(false)
  }

  const updateValue = (event: ChangeEvent<HTMLInputElement>) => {
    handleResize()
    setAddressSearchValue(event.target.value)
    setValue(event.target.value)
    const customValue = {
      target: {
        name: 'searchValue',
        value: event.target.value
      }
    }
    debouncedChangeHandler(customValue) //event
  }

  const debouncedChangeHandler = useCallback(
    debounce((event) => handleChange(event), 650),
    []
  )

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    setValue(query)
    processAutoCompleteService(query)
  }

  const processAutoCompleteService = (inputValue: string) => {
    if (inputValue) {
      if (inputValue.length < 1) {
        setUnit('')
        setZipcode('')
        setDisplayInputs(false)
      } else {
        setDisplayInputs(true)
      }
      const service = new google.maps.places.AutocompleteService()
      const config = {
        input: inputValue,
        componentRestrictions: {
          country: 'us'
        }
      } as google.maps.places.AutocompletionRequest
      service.getPlacePredictions(
        config,
        //@ts-ignore
        function (predictions: google.maps.places.AutocompletePrediction[], status: string) {
          if (status != google.maps.places.PlacesServiceStatus.OK) {
            return
          }
          const predictionList = []
          if (predictions) {
            for (const element of predictions) {
              predictionList.push({
                label: element.description,
                value: element.description,
                boldPart: element.description.substring(0, element.description.indexOf(',')),
                normalPart: element.description.substring(element.description.indexOf(',') + 1),
                id: element.place_id
              } as defaultSuggestion)
            }
          }
          //@ts-ignore
          setSuggestions(predictionList)
          setSuggestionsActive(true)
        }
      )
    } else {
      setDisplayInputs(false)
      setSuggestions([])
      setSuggestionsActive(false)
    }
  }

  const onSubmit = () => {
    if (addressSearchValue.trim() === '' || zipcode.trim() === '') {
      return false
    }

    let apiRequest = {}

    if (selectedAddress) {
      apiRequest = {
        address: selectedAddress.address,
        apt: selectedAddress.apt,
        zipCode: selectedAddress.zipCode,
        city: selectedAddress.city,
        state: selectedAddress.state
      }
    } else {
      setSuggestionsActive(false)
      apiRequest = {
        address: addressSearchValue || value,
        apt: unit,
        zipCode: zipcode,
        city: city,
        state: state
      }
    }

    setSearchProgress(true)

    apiRequest = {
      ...apiRequest,
      lat: latValue,
      lon: langValue
    }

    const apiEndpoint = WEB_APIS.OUTAGE_MAP_UNAUTHENTICATED

    axios
      .post(apiEndpoint, apiRequest, { withCredentials: true })
      .then((response) => {
        const addressResponse = response?.data ?? null
        setSearchCompleted(true)

        if (addressResponse?.multiAddress) {
          const addrList = addressResponse?.addresses ?? null
          const formattedList = sortArrayByKey(addrList, 'apt')

          setResponseKey(AddressServiceabilityStatus.multiple)
          setIsMultipleAddress(true)
          setAddressList(formattedList)
          preSelectDropdown(formattedList)
          updateTealiumStatus(ReactComponents.REACT_ADDRESSCAPTURE, 'addressOutageMultiAddressFound')
          bindEditAddressEvent(AddressServiceabilityStatus.multiple, formattedList)
        } else {
          setResponseKey(AddressServiceabilityStatus.default)
          setIsMultipleAddress(false)
        }

        setShowSearchAddressInput(false)
        setSearchResponse(addressResponse)
      })
      .catch((error) => {
        console.error('Address Capture Serviceability error: ', error)
        setSearchCompleted(false)
      })
      .finally(() => {
        setSearchProgress(false)
      })
  }

  const getOutageMapResponse = () => {
    const updatedSearchResponse = searchResponse?.message?.replace(/<br\s*\/?>/gi, '')

    if (searchResponse?.success && searchResponse?.activeAddress) {
      updateTealiumStatus(ReactComponents.REACT_ADDRESSCAPTURE, 'addressOutageNotFound')
      return (
        <Banner
          bannerType={BannerType.DYNAMIC}
          message={updatedSearchResponse}
          variation={BannerVariaion.SUCCESS}
          iconPath={CIRCLE_CHECK_LIME_GREEN_CIRCLE}
        ></Banner>
      )
    }

    if (searchResponse?.outageFound && searchResponse?.activeAddress) {
      updateTealiumStatus(ReactComponents.REACT_ADDRESSCAPTURE, 'addressOutageFound')
      return (
        <Banner
          bannerType={BannerType.DYNAMIC}
          message={updatedSearchResponse}
          variation={BannerVariaion.ERROR}
          iconPath={CIRCLE_EXCLAMATION_ERROR}
        ></Banner>
      )
    }

    if (
      (!searchResponse?.success && searchResponse?.errorMessage) ||
      (!searchResponse?.activeAddress && searchResponse?.success && searchResponse?.serviceable)
    ) {
      updateTealiumStatus(ReactComponents.REACT_ADDRESSCAPTURE, 'addressOutageNoMatchFound')
      const parser = new DOMParser()
      const doc = parser.parseFromString(updatedSearchResponse, 'text/html')
      const serviceAddressLink = doc.querySelector('a')
      serviceAddressLink?.classList.add('show-search-field')
      const updatedMessage = doc.body.innerHTML

      return (
        <Banner
          bannerType={BannerType.DYNAMIC}
          message={updatedMessage}
          variation={BannerVariaion.ERROR}
          iconPath={CIRCLE_EXCLAMATION_ERROR}
        ></Banner>
      )
    }

    // server error
    updateTealiumStatus(ReactComponents.REACT_ADDRESSCAPTURE, 'addressOutageServerError')
    return (
      <Banner
        bannerType={BannerType.DYNAMIC}
        message={OUTAGE_MAP_SERVER_ERROR_MESSAGE}
        variation={BannerVariaion.ERROR}
        iconPath={CIRCLE_EXCLAMATION_ERROR}
      ></Banner>
    )
  }

  const preSelectDropdown = (addressList: any) => {
    //pre-select first item in multiple address dropdown
    if (addressList && addressList.length > 0) {
      const { address = '', apt = '', zipCode = '', city = '', state = '' }: addressFormat = addressList[0]
      handleSelect(address, apt, zipCode, city, state)
    }
  }

  const bindEditAddressEvent = (addressStatus: string, multipleAddressList: any | null) => {
    if (addressStatus == AddressServiceabilityStatus.multiple) {
      const { address: editAddressText, zipCode: editZipcode } =
        multipleAddressList && multipleAddressList.length > 0 ? multipleAddressList[0] : { address: '', zipCode: '' }
      setTimeout(() => {
        const descriptionSection = ReactDOM.findDOMNode(descriptionRef.current) as HTMLDivElement
        if (descriptionSection) {
          const childNode = id && document.querySelector('a[title=' + id + ']')
          if (childNode) {
            childNode.addEventListener('click', (event) => {
              event.preventDefault()
              retainDefaultState(editAddressText, '', editZipcode)
            })
          }
        }
      }, 350)
    }
  }

  const retainDefaultState = (address: string, apt: string, zipcode: string) => {
    setResponseKey(AddressServiceabilityStatus.default)
    setShowSearchAddressInput(true)
    setIsMultipleAddress(false)
    setSearchProgress(false)
    setSearchCompleted(false)
    setDisplayInputs(true)
    setValue(address)
    setUnit(apt)
    setZipcode(zipcode)
    setSelectedAddress(null)
  }

  useEffect(() => {
    getProcessedResponse(responseKey)
  }, [responseKey])

  const getProcessedResponse = (key: string): void => {
    if (messages) {
      try {
        const filterJson = JSON.parse(messages)
        if (filterJson[key]) {
          setEyebrow(
            filterJson[key].eyebrow
              ? (getTokenValues(filterJson[key].eyebrow, tokenProperties, udo?.localeName) as string)
              : ''
          )
          setHeading(
            filterJson[key].heading
              ? (getTokenValues(filterJson[key].heading, tokenProperties, udo?.localeName) as string)
              : ''
          )
          setDescription(
            filterJson[key].description
              ? (getTokenValues(filterJson[key].description, tokenProperties, udo?.localeName) as string)
              : ''
          )

          return filterJson[key]
        }
      } catch (error) {
        console.error('Error in custom message parsing:', error)
      }
    }
    fallbackResponse()
  }

  const fallbackResponse = () => {
    setEyebrow('')
    setHeading('')
    setDescription('')
  }

  const handleSelect = (address: string, apartment: string, zipCode: string, city: string, state: string) => {
    const addressSelected: any = {
      address: address,
      apt: apartment,
      zipCode: zipCode,
      city: city,
      state: state
    }

    const seleactedDropdownValue = `${address}, #${apartment}, ${zipCode}, ${city}, ${state}`
    setSelectedAddress(addressSelected)
    triggerGeocodeCall(seleactedDropdownValue, true)
  }

  return (
    <div
      className={`react-ac-container ${configuredFor} ${inLayoutContainer ? '' : 'default-container-spacing'} ${theme}`}
      data-testid={`react-outagemap-ac-container`}
    >
      <div className='react-ac-search-header'>
        {eyebrow && <ReactText text={eyebrow} isParsed={true} className='react-ac-eyebrow' id={'react-ac-eyebrow'} />}

        {heading && <ReactText text={heading} isParsed={true} className='react-ac-heading' id={'react-ac-heading'} />}

        {description && (
          <div ref={descriptionRef}>
            <ReactText text={description} isParsed={true} className='react-ac-description' id={'react-ac-description'} />
          </div>
        )}
      </div>

      <div className='react-ac-search-block'>
        {showSearchAddressInput && !isMultipleAddress && (
          <AddressCaptureInput
            ref={inputRef}
            configuredFor={configuredFor}
            theme={theme}
            locationPin={locationPin}
            simplifiedIcon={simplifiedIcon}
            value={value}
            unit={unit}
            zipcode={zipcode}
            searchProgress={searchProgress}
            searchCompleted={searchCompleted}
            displayInputs={displayInputs}
            streetInputPlaceholder={streetInputPlaceholder}
            updateValue={updateValue}
            setUnit={setUnit}
            setZipcode={setZipcode}
            setSuggestionsActive={setSuggestionsActive}
            handleKeyDown={handleKeyDown}
            onSubmit={onSubmit}
          />
        )}

        {!showSearchAddressInput && !isMultipleAddress && getOutageMapResponse()}

        {isMultipleAddress && (
          <MultipleAddressDropdown
            addressList={addressList}
            configuredFor={configuredFor}
            selectedAddress={selectedAddress}
            multipleAddressChevron={multipleAddressChevron}
            multipleAddressChevronAlt={multipleAddressChevronAlt}
            handleSelect={handleSelect}
            onSubmit={onSubmit}
          />
        )}
      </div>

      {suggestionsActive && suggestions && suggestions.length > 0 && <Suggestions />}
    </div>
  )
}

export default AddressCaptureOutageMap
